import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Divider,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  CardHeader,
  Switch,
  Chip
} from '@mui/material';
import {
  Save as SaveIcon,
  Send as SendIcon,
  Clear as ClearIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { KundenService } from '../services/kundenService';
import { ZulassungsantragService } from '../services/zulassungsantragService';
import { NutzerService } from '../services/nutzerService';
import { Kunde } from '../types/kunde';
import { Nutzer } from '../types/nutzer';
import { useAuth } from '../context/AuthContext';
import {
  ZulassungsantragFormData, 
  ZulassungsantragResponse,
  Kennzeichenart, 
  AntragStatus,
  GeschaeftsvorfallTyp,
  AntragstellerTyp,
  VALIDATION_RULES,
  FormValidation,
  getGeschaeftsvorfallTypLabel,
  getAntragstellerTypLabel
} from '../types/antrag';

const AntragstellungPage: React.FC = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const editId = searchParams.get('edit');
  const [kunden, setKunden] = useState<Kunde[]>([]);
  const [nutzerDetails, setNutzerDetails] = useState<Nutzer | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [editingAntragId, setEditingAntragId] = useState<number | null>(null);

  // Formular-Daten
  const [formData, setFormData] = useState<ZulassungsantragFormData>({
    // Geschäftsvorfall
    geschaeftsvorfallTyp: 'NEUZULASSUNG',
    antragstellerTyp: 'EIGENE_PERSON',  // Default: Zulassung auf sich selbst
    
    // Halterdaten
    halterTyp: 'natuerlich', // 'natuerlich' | 'juristisch'
    vorname: '',
    nachname: '',
    geburtstag: '',
    geburtsort: '',
    email: '',
    telefonnummer: '',

    // Fahrzeugdaten
    fahrzeugTyp: 'auto', // 'auto' | 'motorrad' | 'traktor'
    fin: '',
    zbIINummer: '',
    zbIISicherheitscode: '',
    briefnummer: '',
    zbIISicherheitscodeZB2: '',
    evb: '',
    alteKennzeichenStadt: '',
    alteKennzeichenBuchstaben: '',
    alteKennzeichenNummern: '',
    neueKennzeichenStadt: '',
    neueKennzeichenBuchstaben: '',
    neueKennzeichenNummern: '',
    sonderkennzeichen: '', // 'H' | 'E' | ''
    reservierungsPin: '',
    saisonkennzeichen: false,
    saisonVon: '',
    saisonBis: '',

    // Bankverbindung
    zulassungsstelle: '',
    datumZulassung: '',
    iban: '',
    bic: '',
    bankName: '',
    bankVorname: '',

    // Alte Felder für Kompatibilität
    gkIdZulB: '',
    kennzeichen: '',
    kennzeichenart: '',
    reservierungsnummer: '',
    elektrokennzeichen: false,
    feinstaubplakette: false,
    vbNummer: '',
    verwendungszweck: '',
    berufsschluessel: '',
    huMonatJahr: '',
    huPruefziffer: '',
    huDatumUntersuchung: '',
    huUeberwachungsorganisation: '',
    versandVorname: '',
    versandName: '',
    versandStrasse: '',
    versandHausnummer: '',
    versandAdressierungszusaetze: '',
    versandPostleitzahl: '',
    versandOrt: '',
    kundeId: 0,
    status: AntragStatus.ERSTELLT
  } as ZulassungsantragFormData);

  const steps = [
    'Allgemeine Antragsdaten',
    'HU-Nachweis',
    'Versandadresse',
    'Prüfung & Absendung'
  ];

  useEffect(() => {
    console.log('useEffect triggered, editId:', editId, 'user:', user);
    loadKunden();
    
    // Check if we have a pre-selected geschaeftsvorfallTyp from navigation
    const stateData = location.state as { geschaeftsvorfallTyp?: string };
    if (stateData?.geschaeftsvorfallTyp) {
      setFormData(prev => ({
        ...prev,
        geschaeftsvorfallTyp: stateData.geschaeftsvorfallTyp as string
      }));
    }
    
    // Check if we're in edit mode first
    const isInEditMode = !!editId;
    console.log('isInEditMode:', isInEditMode);
    
    if (editId) {
      const antragId = parseInt(editId, 10);
      console.log('Parsed antragId:', antragId);
      if (!isNaN(antragId)) {
        setIsEditMode(true);
        setEditingAntragId(antragId);
        loadAntragForEdit(antragId);
      }
    } else {
      setIsEditMode(false);
      setEditingAntragId(null);
    }
    
    // Load nutzer details with edit mode context
    if (user?.id) {
      loadNutzerDetails(user.id, isInEditMode);
    }
  }, [user, editId]);

  const loadKunden = async () => {
    try {
      const response = await KundenService.getAlleKunden(0, 100);
      setKunden(response.content);
    } catch (err) {
      console.error('Fehler beim Laden der Kunden:', err);
      setError('Fehler beim Laden der Kunden');
    }
  };

  const loadNutzerDetails = async (userId: number, isInEditMode: boolean = false) => {
    try {
      const nutzer = await NutzerService.getNutzerById(userId);
      setNutzerDetails(nutzer);
      
      // Automatisch den Kunden des Nutzers vorauswählen (nur wenn nicht im Edit-Modus)
      if (nutzer.kunde && !isInEditMode) {
        setFormData(prev => ({
          ...prev,
          kundeId: nutzer.kunde.id
        }));
      }
    } catch (err) {
      console.error('Fehler beim Laden der Nutzer-Details:', err);
      setError('Fehler beim Laden der Nutzer-Details');
    }
  };

  const loadAntragForEdit = async (antragId: number) => {
    try {
      setLoading(true);
      console.log('Loading antrag for edit, ID:', antragId);
      const antragResponse = await ZulassungsantragService.getZulassungsantragById(antragId);
      console.log('Antrag response:', antragResponse);
      const convertedFormData = convertResponseToFormData(antragResponse);
      console.log('Converted form data:', convertedFormData);
      setFormData(convertedFormData);
      setError(null);
    } catch (err) {
      console.error('Fehler beim Laden des Antrags:', err);
      setError('Fehler beim Laden des Antrags für die Bearbeitung');
    } finally {
      setLoading(false);
    }
  };

  // Convert ZulassungsantragResponse to ZulassungsantragFormData
  const convertResponseToFormData = (response: ZulassungsantragResponse): ZulassungsantragFormData => {
    // Direct JSON conversion - no XML parsing needed
    console.log('Response data to convert:', response);
    
    return {
      // Geschäftsvorfall
      geschaeftsvorfallTyp: response.geschaeftsvorfallTyp || 'NEUZULASSUNG',
      antragstellerTyp: response.antragstellerTyp || 'EIGENE_PERSON',
      
      // Halterdaten
      halterTyp: response.halterTyp || 'natuerlich',
      vorname: response.vorname || '',
      nachname: response.nachname || '',
      geburtstag: response.geburtstag || '',
      geburtsort: response.geburtsort || '',
      email: response.email || '',
      telefonnummer: response.telefonnummer || '',

      // Fahrzeugdaten
      fahrzeugTyp: response.fahrzeugTyp || 'auto',
      fin: response.fin || '',
      zbIINummer: response.zbIINummer || '',
      zbIISicherheitscode: response.zbIISicherheitscode || '',
      reservierungsPin: response.reservierungsPin || '',
      kennzeichenart: response.kennzeichenart || '',
      elektrokennzeichen: response.elektrokennzeichen || false,
      feinstaubplakette: response.feinstaubplakette || false,

      // Bankverbindung
      zulassungsstelle: response.zulassungsstelle || '',
      iban: response.iban || '',
      bic: response.bic || '',
      bankName: response.bankName || '',
      bankVorname: response.bankVorname || '',

      // Alte Felder für Kompatibilität (leer lassen)
      gkIdZulB: '',
      kennzeichen: '',
      reservierungsnummer: '',
      vbNummer: '',
      verwendungszweck: '',
      berufsschluessel: '',

      // HU-Nachweis
      huMonatJahr: response.huMonatJahr || '',
      huPruefziffer: response.huPruefziffer || '',
      huDatumUntersuchung: response.huDatumUntersuchung || '',
      huUeberwachungsorganisation: response.huUeberwachungsorganisation || '',

      // Versandadresse
      versandVorname: response.versandVorname || '',
      versandName: response.versandName || '',
      versandStrasse: response.versandStrasse || '',
      versandHausnummer: response.versandHausnummer || '',
      versandAdressierungszusaetze: response.versandAdressierungszusaetze || '',
      versandPostleitzahl: response.versandPostleitzahl || '',
      versandOrt: response.versandOrt || '',

      // Metadata
      kundeId: response.kunde.id,
      status: response.status as AntragStatus
    };
  };


  const handleInputChange = (field: keyof ZulassungsantragFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Validierung für das geänderte Feld
    validateField(field, value);
  };

  const validateField = (field: keyof ZulassungsantragFormData, value: any) => {
    const rule = VALIDATION_RULES.find(r => r.field === field);
    if (!rule) return;

    let error = '';

    if (rule.required && (!value || value === '' || value === 0)) {
      error = rule.errorMessage;
    } else if (value && rule.pattern && !rule.pattern.test(value.toString())) {
      error = rule.errorMessage;
    } else if (value && rule.minLength && value.toString().length < rule.minLength) {
      error = `Mindestens ${rule.minLength} Zeichen erforderlich`;
    } else if (value && rule.maxLength && value.toString().length > rule.maxLength) {
      error = `Maximal ${rule.maxLength} Zeichen erlaubt`;
    }

    setValidationErrors(prev => ({
      ...prev,
      [field]: error
    }));
  };

  const validateAllFields = (): boolean => {
    let isValid = true;
    const errors: Record<string, string> = {};

    VALIDATION_RULES.forEach(rule => {
      const value = formData[rule.field];
      let error = '';

      if (rule.required && (!value || value === '' || value === 0)) {
        error = rule.errorMessage;
        isValid = false;
      } else if (value && rule.pattern && !rule.pattern.test(value.toString())) {
        error = rule.errorMessage;
        isValid = false;
      }

      if (error) {
        errors[rule.field] = error;
      }
    });

    setValidationErrors(errors);
    return isValid;
  };

  const handleNext = () => {
    // Validierung für den aktuellen Schritt
    if (validateCurrentStep()) {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const validateCurrentStep = (): boolean => {
    // Vereinfachte Validierung pro Schritt
    switch (activeStep) {
      case 0: // Allgemeine Antragsdaten
        // Halterdaten
        const halterValid = !!(formData.vorname && formData.nachname);
        // Fahrzeugdaten
        const fahrzeugValid = !!(formData.fin && formData.zbIINummer && formData.zbIISicherheitscode);
        // Bankverbindung
        const bankValid = !!(formData.zulassungsstelle && formData.iban && formData.bankName);
        // Kunde
        const kundeValid = formData.kundeId > 0;
        
        return halterValid && fahrzeugValid && bankValid && kundeValid;
      case 1: // HU-Nachweis (alle fakultativ)
        return true;
      case 2: // Versandadresse
        return !!(formData.versandName && formData.versandStrasse && 
               formData.versandHausnummer && formData.versandPostleitzahl && formData.versandOrt);
      default:
        return true;
    }
  };

  const handleSubmit = async (isDraft: boolean = false) => {
    if (!isDraft && !validateAllFields()) {
      setError('Bitte korrigieren Sie die Eingabefehler');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Validierung vor dem Senden
      const validationErrors = ZulassungsantragService.validateFormData(formData);
      if (!isDraft && validationErrors.length > 0) {
        setError(`Validierungsfehler: ${validationErrors.join(', ')}`);
        setLoading(false);
        return;
      }

      // Status setzen
      const updatedFormData = {
        ...formData,
        status: isDraft ? AntragStatus.ERSTELLT : AntragStatus.GESENDET
      };
      
      let response;
      if (isEditMode && editingAntragId) {
        // API-Call zum Aktualisieren des Antrags
        response = await ZulassungsantragService.updateZulassungsantrag(editingAntragId, updatedFormData);
        setSuccess(
          isDraft 
            ? `Antrag erfolgreich aktualisiert und als Entwurf gespeichert (ID: ${response.id})` 
            : `Antrag erfolgreich aktualisiert und eingereicht (ID: ${response.id}, Nachrichten-ID: ${response.nachrichtenId})`
        );
      } else {
        // API-Call zum Erstellen des Antrags
        response = await ZulassungsantragService.createZulassungsantrag(updatedFormData);
        setSuccess(
          isDraft 
            ? `Antrag als Entwurf gespeichert (ID: ${response.id})` 
            : `Antrag erfolgreich eingereicht (ID: ${response.id}, Nachrichten-ID: ${response.nachrichtenId})`
        );
      }
      
      if (!isDraft) {
        // Nach erfolgreichem Einreichen zurück zum Dashboard
        setTimeout(() => {
          window.location.href = '/dashboard';
        }, 3000);
      }
    } catch (err: any) {
      console.error('Fehler beim Speichern des Antrags:', err);
      setError(
        err.response?.data?.message || 
        err.message || 
        'Fehler beim Speichern des Antrags'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    if (window.confirm('Möchten Sie wirklich alle Eingaben löschen?')) {
      setFormData({
        // Geschäftsvorfall
        geschaeftsvorfallTyp: 'NEUZULASSUNG',
        antragstellerTyp: 'EIGENE_PERSON',
        
        // Halterdaten
        halterTyp: 'natuerlich',
        vorname: '',
        nachname: '',
        geburtstag: '',
        geburtsort: '',
        email: '',
        telefonnummer: '',

        // Fahrzeugdaten
        fahrzeugTyp: 'auto',
        fin: '',
        zbIINummer: '',
        zbIISicherheitscode: '',
        briefnummer: '',
        zbIISicherheitscodeZB2: '',
        evb: '',
        alteKennzeichenStadt: '',
        alteKennzeichenBuchstaben: '',
        alteKennzeichenNummern: '',
        neueKennzeichenStadt: '',
        neueKennzeichenBuchstaben: '',
        neueKennzeichenNummern: '',
        sonderkennzeichen: '',
        reservierungsPin: '',
        saisonkennzeichen: false,
        saisonVon: '',
        saisonBis: '',

        // Bankverbindung
        zulassungsstelle: '',
        datumZulassung: '',
        iban: '',
        bic: '',
        bankName: '',
        bankVorname: '',

        // Alte Felder für Kompatibilität
        gkIdZulB: '',
        kennzeichen: '',
        kennzeichenart: '',
        reservierungsnummer: '',
        elektrokennzeichen: false,
        feinstaubplakette: false,
        vbNummer: '',
        verwendungszweck: '',
        berufsschluessel: '',
        huMonatJahr: '',
        huPruefziffer: '',
        huDatumUntersuchung: '',
        huUeberwachungsorganisation: '',
        versandVorname: '',
        versandName: '',
        versandStrasse: '',
        versandHausnummer: '',
        versandAdressierungszusaetze: '',
        versandPostleitzahl: '',
        versandOrt: '',
        kundeId: 0,
        status: AntragStatus.ERSTELLT
      } as ZulassungsantragFormData);
      setValidationErrors({});
      setActiveStep(0);
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return renderAllgemeineAntragsdaten();
      case 1:
        return renderHUNachweis();
      case 2:
        return renderVersandadresse();
      case 3:
        return renderPruefungUndAbsendung();
      default:
        return null;
    }
  };

  const getSelectedKunde = () => {
    // Zuerst versuchen, den Kunden aus den nutzerDetails zu nehmen
    if (nutzerDetails?.kunde && formData.kundeId === nutzerDetails.kunde.id) {
      return nutzerDetails.kunde;
    }
    // Fallback: Kunden aus der Liste suchen
    return kunden.find(k => k.id === formData.kundeId);
  };

  const renderAllgemeineAntragsdaten = () => (
    <Box>
      {/* Sachbearbeiter und Kunde Info - nicht editierbar */}
      <Card elevation={1} sx={{ mb: 3, backgroundColor: '#f5f5f5' }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Sachbearbeiter"
                value={nutzerDetails ? `${nutzerDetails.vorname} ${nutzerDetails.nachname} (${nutzerDetails.username})` : user?.username || ''}
                inputProps={{ readOnly: true }}
                variant="filled"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Kundenname"
                value={getSelectedKunde()?.name || 'Kein Kunde ausgewählt'}
                inputProps={{ readOnly: true }}
                variant="filled"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>



      {/* Geschäftsvorfall */}
      <Card elevation={2} sx={{ mb: 3 }}>
        <CardHeader
          title="Geschäftsvorfall"
          subheader="Art des beantragten Geschäftsvorfalls"
        />
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Geschäftsvorfall-Typ *</InputLabel>
                <Select
                  value={formData.geschaeftsvorfallTyp}
                  onChange={(e) => handleInputChange('geschaeftsvorfallTyp', e.target.value)}
                  label="Geschäftsvorfall-Typ *"
                  error={!!validationErrors.geschaeftsvorfallTyp}
                >
                  {Object.values(GeschaeftsvorfallTyp).map((typ) => (
                    <MenuItem key={typ} value={typ}>
                      {getGeschaeftsvorfallTypLabel(typ)}
                    </MenuItem>
                  ))}
                </Select>
                {validationErrors.geschaeftsvorfallTyp && (
                  <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                    {validationErrors.geschaeftsvorfallTyp}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Zulassung auf *</InputLabel>
                <Select
                  value={formData.antragstellerTyp || 'EIGENE_PERSON'}
                  onChange={(e) => handleInputChange('antragstellerTyp', e.target.value)}
                  label="Zulassung auf *"
                >
                  <MenuItem value={AntragstellerTyp.EIGENE_PERSON}>
                    {getAntragstellerTypLabel(AntragstellerTyp.EIGENE_PERSON)}
                  </MenuItem>
                  <MenuItem value={AntragstellerTyp.DRITTE_PERSON}>
                    {getAntragstellerTypLabel(AntragstellerTyp.DRITTE_PERSON)}
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Halterdaten */}
      <Card elevation={2} sx={{ mb: 3 }}>
        <CardHeader
          title="Halterdaten"
          subheader="Angaben zur Person des Fahrzeughalters"
        />
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Typ *</InputLabel>
                <Select
                  value={formData.halterTyp}
                  onChange={(e) => handleInputChange('halterTyp', e.target.value)}
                  label="Typ *"
                >
                  <MenuItem value="natuerlich">Natürliche Person</MenuItem>
                  <MenuItem value="juristisch">Juristische Person</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Vorname *"
                value={formData.vorname}
                onChange={(e) => handleInputChange('vorname', e.target.value)}
                error={!!validationErrors.vorname}
                helperText={validationErrors.vorname}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nachname *"
                value={formData.nachname}
                onChange={(e) => handleInputChange('nachname', e.target.value)}
                error={!!validationErrors.nachname}
                helperText={validationErrors.nachname}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Geburtstag"
                type="date"
                value={formData.geburtstag}
                onChange={(e) => handleInputChange('geburtstag', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Geburtsort"
                value={formData.geburtsort}
                onChange={(e) => handleInputChange('geburtsort', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="E-Mail"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                error={!!validationErrors.email}
                helperText={validationErrors.email}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Telefonnummer"
                value={formData.telefonnummer}
                onChange={(e) => handleInputChange('telefonnummer', e.target.value)}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Fahrzeugdaten */}
      <Card elevation={2} sx={{ mb: 3 }}>
        <CardHeader
          title="Fahrzeugdaten"
          subheader="Technische Daten und Kennzeichen des Fahrzeugs"
        />
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Fahrzeugtyp *</InputLabel>
                <Select
                  value={formData.fahrzeugTyp}
                  onChange={(e) => handleInputChange('fahrzeugTyp', e.target.value)}
                  label="Fahrzeugtyp *"
                >
                  <MenuItem value="auto">Auto</MenuItem>
                  <MenuItem value="motorrad">Motorrad</MenuItem>
                  <MenuItem value="traktor">Traktor</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="FIN (Fahrzeug-Identifizierungsnummer) *"
                value={formData.fin}
                onChange={(e) => handleInputChange('fin', e.target.value.toUpperCase())}
                error={!!validationErrors.fin}
                helperText={validationErrors.fin || "17 Zeichen, ohne I, O, Q"}
                placeholder="z.B. WVWZZZ1JZ3W386752"
                inputProps={{ maxLength: 17 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="ZB1 Nummer *"
                value={formData.zbIINummer}
                onChange={(e) => handleInputChange('zbIINummer', e.target.value)}
                error={!!validationErrors.zbIINummer}
                helperText={validationErrors.zbIINummer}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Sicherheitscode ZB1 *"
                value={formData.zbIISicherheitscode}
                onChange={(e) => handleInputChange('zbIISicherheitscode', e.target.value)}
                error={!!validationErrors.zbIISicherheitscode}
                helperText={validationErrors.zbIISicherheitscode}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Briefnummer"
                value={formData.briefnummer}
                onChange={(e) => handleInputChange('briefnummer', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Sicherheitscode ZB2"
                value={formData.zbIISicherheitscodeZB2}
                onChange={(e) => handleInputChange('zbIISicherheitscodeZB2', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="eVB"
                value={formData.evb}
                onChange={(e) => handleInputChange('evb', e.target.value)}
                helperText="Elektronische Versicherungsbestätigung"
              />
            </Grid>
            
            {/* Alte Kennzeichen */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>Alte Kennzeichen (falls vorhanden)</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Stadtbuchstaben"
                value={formData.alteKennzeichenStadt}
                onChange={(e) => handleInputChange('alteKennzeichenStadt', e.target.value.toUpperCase())}
                placeholder="z.B. B"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Buchstaben"
                value={formData.alteKennzeichenBuchstaben}
                onChange={(e) => handleInputChange('alteKennzeichenBuchstaben', e.target.value.toUpperCase())}
                placeholder="z.B. AB"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Nummern"
                value={formData.alteKennzeichenNummern}
                onChange={(e) => handleInputChange('alteKennzeichenNummern', e.target.value)}
                placeholder="z.B. 1234"
              />
            </Grid>
            
            {/* Neue Kennzeichen */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>Neue Kennzeichen (falls reserviert)</Typography>
            </Grid>
            <Grid item xs={12} md={2.4}>
              <TextField
                fullWidth
                label="Stadtbuchstaben"
                value={formData.neueKennzeichenStadt}
                onChange={(e) => handleInputChange('neueKennzeichenStadt', e.target.value.toUpperCase())}
                placeholder="z.B. B"
              />
            </Grid>
            <Grid item xs={12} md={2.4}>
              <TextField
                fullWidth
                label="Buchstaben"
                value={formData.neueKennzeichenBuchstaben}
                onChange={(e) => handleInputChange('neueKennzeichenBuchstaben', e.target.value.toUpperCase())}
                placeholder="z.B. AB"
              />
            </Grid>
            <Grid item xs={12} md={2.4}>
              <TextField
                fullWidth
                label="Nummern"
                value={formData.neueKennzeichenNummern}
                onChange={(e) => handleInputChange('neueKennzeichenNummern', e.target.value)}
                placeholder="z.B. 1234"
              />
            </Grid>
            <Grid item xs={12} md={2.4}>
              <FormControl fullWidth>
                <InputLabel>Sonderkennzeichen</InputLabel>
                <Select
                  value={formData.sonderkennzeichen}
                  onChange={(e) => handleInputChange('sonderkennzeichen', e.target.value)}
                  label="Sonderkennzeichen"
                >
                  <MenuItem value="">Keines</MenuItem>
                  <MenuItem value="H">H (Historisch)</MenuItem>
                  <MenuItem value="E">E (Elektro)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2.4}>
              <TextField
                fullWidth
                label="PIN der Reservierung"
                value={formData.reservierungsPin}
                onChange={(e) => handleInputChange('reservierungsPin', e.target.value)}
              />
            </Grid>
            
            {/* Saisonkennzeichen */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.saisonkennzeichen}
                    onChange={(e) => handleInputChange('saisonkennzeichen', e.target.checked)}
                    disabled={!formData.neueKennzeichenStadt || !formData.neueKennzeichenBuchstaben || !formData.neueKennzeichenNummern}
                  />
                }
                label="Saisonkennzeichen"
              />
            </Grid>
            {formData.saisonkennzeichen && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Saison von (Monat)"
                    type="number"
                    value={formData.saisonVon}
                    onChange={(e) => handleInputChange('saisonVon', e.target.value)}
                    inputProps={{ min: 1, max: 12 }}
                    helperText="Monat (1-12)"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Saison bis (Monat)"
                    type="number"
                    value={formData.saisonBis}
                    onChange={(e) => handleInputChange('saisonBis', e.target.value)}
                    inputProps={{ min: 1, max: 12 }}
                    helperText="Monat (1-12)"
                  />
                </Grid>
              </>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Bankverbindung */}
      <Card elevation={2}>
        <CardHeader
          title="Bankverbindung"
          subheader="Zulassungsstelle und Bankdaten"
        />
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Zulassungsstelle *"
                value={formData.zulassungsstelle}
                onChange={(e) => handleInputChange('zulassungsstelle', e.target.value)}
                error={!!validationErrors.zulassungsstelle}
                helperText={validationErrors.zulassungsstelle}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Datum der Zulassung"
                type="date"
                value={formData.datumZulassung}
                onChange={(e) => handleInputChange('datumZulassung', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="IBAN *"
                value={formData.iban}
                onChange={(e) => handleInputChange('iban', e.target.value.toUpperCase())}
                error={!!validationErrors.iban}
                helperText={validationErrors.iban}
                placeholder="z.B. DE89 3704 0044 0532 0130 00"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="BIC"
                value={formData.bic}
                onChange={(e) => handleInputChange('bic', e.target.value.toUpperCase())}
                placeholder="z.B. COBADEFFXXX"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Name (Kontoinhaber) *"
                value={formData.bankName}
                onChange={(e) => handleInputChange('bankName', e.target.value)}
                error={!!validationErrors.bankName}
                helperText={validationErrors.bankName}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Vorname (Kontoinhaber)"
                value={formData.bankVorname}
                onChange={(e) => handleInputChange('bankVorname', e.target.value)}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );

  const renderHUNachweis = () => (
    <Card elevation={2}>
      <CardHeader
        title="HU-Nachweis"
        subheader="Hauptuntersuchung (alle Felder optional)"
      />
      <CardContent>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Monat und Jahr der nächsten HU"
              value={formData.huMonatJahr || ''}
              onChange={(e) => handleInputChange('huMonatJahr', e.target.value)}
              placeholder="z.B. 12/2025"
              helperText="Format: MM/YYYY"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="HU-Prüfziffer"
              value={formData.huPruefziffer || ''}
              onChange={(e) => handleInputChange('huPruefziffer', e.target.value)}
              error={!!validationErrors.huPruefziffer}
              helperText={validationErrors.huPruefziffer || "8 Ziffern"}
              placeholder="z.B. 12345678"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="date"
              label="Datum der Untersuchung"
              value={formData.huDatumUntersuchung || ''}
              onChange={(e) => handleInputChange('huDatumUntersuchung', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Nummer der Überwachungsorganisation (ÜI)"
              value={formData.huUeberwachungsorganisation || ''}
              onChange={(e) => handleInputChange('huUeberwachungsorganisation', e.target.value)}
              placeholder="z.B. TÜV001"
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderVersandadresse = () => (
    <Card elevation={2}>
      <CardHeader
        title="Alternative Versandadresse"
        subheader="Adresse für den Versand der Zulassungsdokumente"
      />
      <CardContent>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Vorname"
              value={formData.versandVorname || ''}
              onChange={(e) => handleInputChange('versandVorname', e.target.value)}
              placeholder="z.B. Max"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Familienname / Name jur. Person *"
              value={formData.versandName}
              onChange={(e) => handleInputChange('versandName', e.target.value)}
              error={!!validationErrors.versandName}
              helperText={validationErrors.versandName}
              placeholder="z.B. Mustermann oder Mustermann GmbH"
            />
          </Grid>

          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              label="Straße *"
              value={formData.versandStrasse}
              onChange={(e) => handleInputChange('versandStrasse', e.target.value)}
              error={!!validationErrors.versandStrasse}
              helperText={validationErrors.versandStrasse}
              placeholder="z.B. Musterstraße"
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Hausnummer *"
              value={formData.versandHausnummer}
              onChange={(e) => handleInputChange('versandHausnummer', e.target.value)}
              error={!!validationErrors.versandHausnummer}
              helperText={validationErrors.versandHausnummer}
              placeholder="z.B. 123a"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Adressierungszusätze"
              value={formData.versandAdressierungszusaetze || ''}
              onChange={(e) => handleInputChange('versandAdressierungszusaetze', e.target.value)}
              placeholder="z.B. c/o Firma XYZ, 2. Stock"
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Postleitzahl *"
              value={formData.versandPostleitzahl}
              onChange={(e) => handleInputChange('versandPostleitzahl', e.target.value)}
              error={!!validationErrors.versandPostleitzahl}
              helperText={validationErrors.versandPostleitzahl}
              placeholder="z.B. 12345"
              inputProps={{ maxLength: 5 }}
            />
          </Grid>

          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              label="Ort *"
              value={formData.versandOrt}
              onChange={(e) => handleInputChange('versandOrt', e.target.value)}
              error={!!validationErrors.versandOrt}
              helperText={validationErrors.versandOrt}
              placeholder="z.B. Berlin"
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderPruefungUndAbsendung = () => (
    <Card elevation={2}>
      <CardHeader
        title="Prüfung und Absendung"
        subheader="Überprüfen Sie Ihre Angaben vor der Einreichung"
      />
      <CardContent>
        <Box mb={3}>
          <Typography variant="h6" gutterBottom>
            Zusammenfassung der Antragsdaten
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="textSecondary">Kunde:</Typography>
              <Typography variant="body1">
                {kunden.find(k => k.id === formData.kundeId)?.name || 'Nicht ausgewählt'}
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="textSecondary">FIN:</Typography>
              <Typography variant="body1">{formData.fin || 'Nicht angegeben'}</Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="textSecondary">Kennzeichen:</Typography>
              <Typography variant="body1">{formData.kennzeichen || 'Nicht angegeben'}</Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="textSecondary">Versandadresse:</Typography>
              <Typography variant="body1">
                {formData.versandName ? 
                  `${formData.versandName}, ${formData.versandStrasse} ${formData.versandHausnummer}, ${formData.versandPostleitzahl} ${formData.versandOrt}` 
                  : 'Nicht vollständig'
                }
              </Typography>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box display="flex" gap={2} flexWrap="wrap">
          <Button
            variant="outlined"
            startIcon={<SaveIcon />}
            onClick={() => handleSubmit(true)}
            disabled={loading}
          >
            Als Entwurf speichern
          </Button>
          
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            onClick={() => handleSubmit(false)}
            disabled={loading || !validateCurrentStep()}
            color="primary"
          >
            Antrag einreichen
          </Button>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          {isEditMode ? 'Zulassungsantrag bearbeiten' : 'Zulassungsantrag stellen'}
        </Typography>
        <Button
          variant="outlined"
          startIcon={<ClearIcon />}
          onClick={handleClear}
          color="warning"
        >
          Alle Felder löschen
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Stepper */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* Formular-Inhalt */}
      <Box mb={3}>
        {renderStepContent(activeStep)}
      </Box>

      {/* Navigation */}
      <Paper elevation={1} sx={{ p: 2 }}>
        <Box display="flex" justifyContent="space-between">
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
          >
            Zurück
          </Button>
          
          {activeStep < steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={!validateCurrentStep()}
            >
              Weiter
            </Button>
          ) : null}
        </Box>
      </Paper>
    </Box>
  );
};

export default AntragstellungPage;
